<template>
  <!-- 사이드바 최상단 유틸리티 바: 로그아웃 · 테마 토글(좌) + 접기/펼치기(우) -->
  <div class="utility-bar d-flex pa-1" :class="isMini ? 'justify-center' : 'align-center justify-space-between'">
    <!-- 접힘 시 로그아웃·테마는 숨기고 펼치기 버튼만 노출(다시 펼칠 수 있게) -->
    <div v-if="!isMini" class="d-flex">
      <v-btn icon small @click="showLogoutDialog = true" :disabled="isModelLoading" :title="$t('nav.logout')">
        <v-icon :size="22">mdi-logout</v-icon>
      </v-btn>
      <v-btn icon small @click="toggleTheme" :title="isDark ? $t('nav.themeLight') : $t('nav.themeDark')">
        <v-icon :size="22">{{ isDark ? "mdi-weather-night" : "mdi-weather-sunny" }}</v-icon>
      </v-btn>
      <v-btn icon small @click="showLanguageDialog = true" :title="$t('nav.language')">
        <v-icon :size="22">mdi-translate</v-icon>
      </v-btn>
      <v-btn icon small @click="openCountryDialog" :title="$t('nav.country')">
        <span v-if="currentCountryEmoji" class="utility-bar__flag">{{ currentCountryEmoji }}</span>
        <v-icon v-else :size="22">mdi-flag-outline</v-icon>
      </v-btn>
    </div>
    <v-btn icon small @click="$emit('toggle-mini')" :title="isMini ? $t('nav.expand') : $t('nav.collapse')">
      <v-icon :size="22">{{ isMini ? "mdi-chevron-right" : "mdi-chevron-left" }}</v-icon>
    </v-btn>

    <!-- 언어 설정 다이얼로그 (공용 BaseDialog) -->
    <base-dialog v-model="showLanguageDialog" :title="$t('nav.languageTitle')">
      <v-list class="py-0" dense>
        <v-list-item
          v-for="lang in languageItems"
          :key="lang.value"
          :input-value="lang.value === currentLocale"
          color="primary"
          @click="onSelectLanguage(lang.value)"
        >
          <v-list-item-content>
            <v-list-item-title>{{ lang.text }}</v-list-item-title>
          </v-list-item-content>
          <v-list-item-icon v-if="lang.value === currentLocale">
            <v-icon color="primary">mdi-check</v-icon>
          </v-list-item-icon>
        </v-list-item>
      </v-list>
    </base-dialog>

    <!-- 국가 설정 다이얼로그 (검색 + 플래그 이모지 목록, 공용 BaseDialog) -->
    <base-dialog v-model="showCountryDialog" :title="$t('nav.countryTitle')">
      <v-text-field
        v-model="countrySearch"
        :placeholder="$t('nav.countrySearch')"
        prepend-inner-icon="mdi-magnify"
        dense
        outlined
        hide-details
        clearable
        autofocus
        class="mb-2"
      ></v-text-field>
      <div class="country-list">
        <v-list class="py-0" dense>
          <v-list-item
            v-for="c in filteredCountries"
            :key="c.code"
            :input-value="c.code === userCountryCode"
            color="primary"
            @click="onSelectCountry(c.code)"
          >
            <v-list-item-icon class="mr-3">
              <span class="country-list__flag">{{ c.emoji }}</span>
            </v-list-item-icon>
            <v-list-item-content>
              <v-list-item-title>{{ c.name }}</v-list-item-title>
            </v-list-item-content>
            <v-list-item-icon v-if="c.code === userCountryCode">
              <v-icon color="primary">mdi-check</v-icon>
            </v-list-item-icon>
          </v-list-item>
        </v-list>
      </div>
    </base-dialog>

    <!-- 로그아웃 확인 다이얼로그 (공용 BaseDialog) -->
    <base-dialog v-model="showLogoutDialog" :title="$t('nav.logoutConfirmTitle')">
      {{ $t("nav.logoutConfirmBody") }}
      <template #actions>
        <v-btn text @click="showLogoutDialog = false">{{ $t("common.cancel") }}</v-btn>
        <v-btn color="primary" text @click="onClickLogout">{{ $t("nav.logout") }}</v-btn>
      </template>
    </base-dialog>
  </div>
</template>

<script>
import { supabase } from "@/utils/supabase";
import { setLocale } from "@/plugins/i18n";
import { listCountries, findCountry } from "@/utils/countries";
import SupabaseApi from "@/api/SupabaseApi";

export default {
  name: "UtilityBar",
  props: {
    isMini: { type: Boolean, default: false },
  },
  data() {
    return {
      showLogoutDialog: false,
      showLanguageDialog: false,
      showCountryDialog: false,
      countrySearch: "",
      languageItems: [
        { text: "한국어", value: "ko" },
        { text: "English", value: "en" },
        { text: "日本語", value: "ja" },
      ],
    };
  },
  computed: {
    isDark() {
      return this.$vuetify.theme.dark;
    },
    currentLocale() {
      return this.$i18n.locale;
    },
    // 사용자 국가 코드 — user_account.country_code 미러(store)
    userCountryCode() {
      return this.$store.state.userCountryCode;
    },
    currentCountryEmoji() {
      const c = findCountry(this.userCountryCode);
      return c ? c.emoji : null;
    },
    // 현재 로케일로 현지화된 전체 국가 목록 (로케일 변경 시 자동 재계산)
    allCountries() {
      return listCountries(this.$i18n.locale);
    },
    filteredCountries() {
      const q = (this.countrySearch || "").trim().toLowerCase();
      if (!q) return this.allCountries;
      return this.allCountries.filter((c) => c.name.toLowerCase().includes(q) || c.code.toLowerCase().includes(q));
    },
    isModelLoading() {
      return ["ModelLoading"].indexOf(this.$route.name) > -1;
    },
  },
  methods: {
    // 라이트/다크 테마 토글 + localStorage 저장 (App.vue 부팅 시 복원)
    toggleTheme() {
      const dark = !this.$vuetify.theme.dark;
      this.$vuetify.theme.dark = dark;
      localStorage.setItem("theme/dark", dark ? "true" : "false");
    },
    // 언어 변경: i18n + Vuetify 로케일 즉시 반영(반응형) → user_account.locale에 영속(localStorage 대체)
    async onSelectLanguage(lang) {
      setLocale(lang);
      this.$vuetify.lang.current = lang;
      this.showLanguageDialog = false;
      try {
        await SupabaseApi.UserAccount.updateLocale(lang);
      } catch (e) {
        console.error("updateLocale failed", e);
      }
    },
    openCountryDialog() {
      this.countrySearch = "";
      this.showCountryDialog = true;
    },
    // 국가 변경: store 즉시 반영 → user_account.country_code에 영속
    async onSelectCountry(code) {
      this.$store.commit("__setUserCountryCode", code);
      this.showCountryDialog = false;
      try {
        await SupabaseApi.UserAccount.updateCountryCode(code);
      } catch (e) {
        console.error("updateCountryCode failed", e);
      }
    },
    async onClickLogout() {
      await supabase.auth.signOut();
      localStorage.clear();
      this.$router.push("/login");
    },
  },
};
</script>

<style scoped>
/* 유틸리티 바 국가 버튼의 플래그 이모지 — v-icon 사이즈(22)와 시각적으로 맞춤 */
.utility-bar__flag {
  font-size: 18px;
  line-height: 1;
}
/* 국가 목록 — 고정 높이로 스크롤(검색 결과 수와 무관하게 다이얼로그 크기 일정) */
.country-list {
  height: 320px;
  overflow-y: auto;
}
.country-list__flag {
  font-size: 20px;
  line-height: 1;
}
</style>
